import { PressableVariants } from '@unistocks/components/basic/variants';
import { withFeedback as withFeedbackFn } from '@unistocks/shared/utils/haptics/with-feedback';
import { isFunction } from '@unistocks/shared/utils/util';
import React, { useState } from 'react';
import { PressableProps as RNPressableProps } from 'react-native';
import {
  PressableStateCallbackType,
  Pressable as RNGHPressable,
  PressableProps as RNGHPressableProps
} from 'react-native-gesture-handler';
import { GetProps, ThemeableStack, styled, useTheme } from 'tamagui';

const PRESSABLE_NAME = 'Pressable';

export type PressableProps = GetProps<typeof Pressable> & Pick<RNPressableProps, 'delayLongPress'>;

export const Pressable = styled(ThemeableStack, {
  name: PRESSABLE_NAME,
  transition: 'quickerLessBouncy',
  animateOnly: ['transform'],
  cursor: 'pointer',

  variants: PressableVariants,

  defaultVariants: {
    pressable: true,
    disabled: false
  }
});

export type NativePressableProps = RNGHPressableProps & {
  withFeedback?: boolean;
  withScale?: boolean;
  hoverStyle?: RNGHPressableProps['style'];
  pressStyle?: RNGHPressableProps['style'];
};

const resolveStyle = (
  styleProp: RNGHPressableProps['style'],
  state: PressableStateCallbackType
) => {
  if (isFunction(styleProp)) {
    return styleProp(state);
  }

  return styleProp;
};

/**
 * A wrapper around React Native Gesture Handler's Pressable that applies Tamagui theming for different states (pressed, focused, hovered).
 */
export const NativePressable: React.FC<NativePressableProps> = ({
  hoverStyle,
  pressStyle,
  withFeedback = false,
  withScale = false,
  style,
  ...props
}) => {
  const theme = useTheme();
  const [hovered, setHovered] = useState(false);

  const onPointerEnter: RNGHPressableProps['onPointerEnter'] = e => {
    setHovered(true);
    props.onPointerEnter?.(e);
  };

  const onPointerLeave: RNGHPressableProps['onPointerLeave'] = e => {
    setHovered(false);
    props.onPointerLeave?.(e);
  };

  const onPressIn: RNGHPressableProps['onPressIn'] = e => {
    if (withFeedback) {
      withFeedbackFn();
    }
    props.onPressIn?.(e);
  };

  return (
    <RNGHPressable
      {...props}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPressIn={onPressIn}
      /* hovered is not passed as a prop here, it comes from react native web types */
      style={({ pressed, hovered: _ }) => {
        const resolvedStyle = resolveStyle(style, { pressed, hovered });
        const resolvedPressStyle = pressed
          ? resolveStyle(pressStyle, { pressed, hovered })
          : undefined;
        const resolvedHoverStyle = hovered
          ? resolveStyle(hoverStyle, { pressed, hovered })
          : undefined;

        return [
          {
            backgroundColor: pressed
              ? theme.backgroundPress.get()
              : hovered
                ? theme.color3.get()
                : theme.background.get(),
            borderColor: pressed
              ? theme.borderColorPress.get()
              : hovered
                ? theme.borderColorHover.get()
                : theme.borderColor.get()
          },
          resolvedStyle,
          resolvedHoverStyle,
          resolvedPressStyle
        ];
      }}
    />
  );
};

NativePressable.displayName = 'NativePressable';
